import URI from 'urijs';
import 'urijs/src/URITemplate';
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { mapValues } from 'lodash';

import { getUbiServicesEndpoint } from '@sitegen/common/utils/ubi-services';
import {
    IConfigureUbisoftAPIServiceOptions,
    ISessionHeadersOptions,
    IStat,
    IStats,
    IUserFriend,
    ICountryCode,
    IUserProducts,
    IUserProduct
} from './ubisoft-api.types';
import { IProductDetails } from '@sitegen/common/types/game-content';
import { ISecondaryStoreItem } from '@sitegen/common/types/secondary-store';
import {
    USER_FRIEND_LIST_URL,
    USER_FRIEND_PROFILE_URL,
    USER_PROFILE_STAT_TEMPLATE,
    USER_DETAIL_INFO_URL,
    USER_PRODUCTS_URL,
    IP_LOCATION_URL,
    GET_PRODUCT_DETAILS_URL,
    GET_SECONDARY_STORE_URL
} from './ubisoft-api.constants';

import { IProfilePlatform, IUserDetailInfo } from 'types/profiles';

/**
 * Provides collection of requests to Ubisoft Services API http://doc-ubiservices.ubisoft.org/public-docs/
 */
export class UbisoftAPIService {
    private static AXIOS_INSTANCE: AxiosInstance;
    private static session: ISessionHeadersOptions;

    /** Provide headers and other options to each request */
    public static configure(options: IConfigureUbisoftAPIServiceOptions): void {
        const headers: object = {
            'Ubi-AppId': options.appId
        };

        const baseURL = getUbiServicesEndpoint(options.env);

        this.AXIOS_INSTANCE = axios.create({ headers, baseURL });
        if (options.session) {
            this.configureSession(options.session);
        }
    }

    public static configureSession(session: ISessionHeadersOptions): void {
        this.session = session;
        this.AXIOS_INSTANCE.interceptors.request.use((config: AxiosRequestConfig) => {
            if (this.session) {
                config.headers['Ubi-SessionId'] = this.session.sessionId;
                config.headers['Authorization'] = `ubi_v1 t=${this.session.ticket}`;
            }
            return config;
        });
    }

    public static configureFakeAPI(): Promise<void> {
        /* eslint-disable-next-line*/
        return import('./__mocks__/ubisoft-api.service.mocks').then((module: any): void => {
            this.getFriendList = module.getFriendList;
            this.getFriendProfile = module.getFriendProfile;
            this.getIPLocation = module.getIPLocation;
            this.getUserProducts = module.getUserProducts;
            this.getProductDetails = module.getProductDetails;
        });
    }

    /** Return list of user's friends as map */
    public static async getFriendList(): Promise<IUserFriend[]> {
        const url: string = new URI(USER_FRIEND_LIST_URL).href();
        const response: { friends: IUserFriend[] } = await this.request({ url, method: 'GET' });
        return response.friends;
    }

    /** Return stat value by its name */
    public static async getStatByName(options: {
        readonly userId: string,
        readonly spaceId: string,
        readonly statName: string
    }): Promise<{[name: string]: string}> {
        const values: object = { userId: options.userId };
        const template: string = URI.expand!(USER_PROFILE_STAT_TEMPLATE, values);
        const url: string = new URI(template)
            .segment(template)
            .setQuery({ spaceId: options.spaceId, statName: options.statName })
            .href();

        const response: IStats = await this.request({ url, method: 'GET' });

        return mapValues(response.stats, (stat: IStat, _name) => stat.value );
    }

    public static async getFriendProfile(options: { readonly UbiAccountId: string, readonly platformType?: string}): Promise<IProfilePlatform> {
        const url = new URI(USER_FRIEND_PROFILE_URL).segment(options.UbiAccountId);
        if (options.platformType) {
            url.setQuery({ platformType: options.platformType });
        }
        return await this.request({ url: url.href(), method: 'GET' });
    }

    public static async getUserInfo(): Promise<IUserDetailInfo> {
        const url: string = new URI(USER_DETAIL_INFO_URL).href();
        return await this.request({ url, method: 'GET' });
    }

    public static async getUserProducts(): Promise<IUserProduct[]> {
        const url: string = new URI(USER_PRODUCTS_URL).href();
        const { products} = await this.request<IUserProducts>({ url, method: 'GET' });
        return products;
    }

    public static async getIPLocation(): Promise<ICountryCode> {
        const url: string = new URI(IP_LOCATION_URL).href();
        return await this.request({ url, method: 'GET' });
    }

    public static async getProductDetails(options: { productMdmId: string, locale: string }): Promise<IProductDetails> {
        const url = new URI(GET_PRODUCT_DETAILS_URL).segment(options.productMdmId).href();
        return await this.request({ url, method: 'GET', headers: { 'Ubi-LocaleCode': options.locale } });
    }

    public static async getSecondaryStoreItem({ spaceId, itemId, locale }: { 
        spaceId: string, 
        itemId: string, 
        locale: string
    }): Promise<ISecondaryStoreItem> {
        const template: string = URI.expand!(GET_SECONDARY_STORE_URL, { spaceId, itemId });
        const url = new URI(template).href();

        return await this.request({ url, method: 'GET', headers: { 'Ubi-LocaleCode': locale } });
    }

    /** Throw status code as a number because no particular error handling is available */
    private static request<T>(options: AxiosRequestConfig): Promise<T> {
        return this.AXIOS_INSTANCE.request({ ...options, responseType: 'json' })
            .then((response: AxiosResponse): T => {
                return response.data;
            }).catch((e: AxiosError): never => {
                throw e.response!.status;
            });
    }
}
